import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Eye, EyeOff, MailCheck } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email, origin_url: window.location.origin });
      toast.success("Verification email resent — check your inbox");
    } catch {
      toast.error("Could not resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="register-page">
        <div className="w-full max-w-md ufr-card rounded-md p-8 text-center" data-testid="verify-email-notice">
          <div className="flex justify-center mb-4"><MailCheck size={44} className="text-[color:var(--ufr-green)]" /></div>
          <h1 className="font-display text-3xl text-white">Check your inbox</h1>
          <p className="text-white/70 text-sm mt-3 leading-relaxed">
            We've sent a verification link to <span className="text-[color:var(--ufr-green)] font-bold">{email}</span>.
            Click it to activate your account, then log in. Don't forget to check your spam folder.
          </p>
          <p className="text-white/55 text-xs mt-4 leading-relaxed" data-testid="verify-support-note">
            If you have not received an email from us verifying your account, please resend the verification email below or contact us on{" "}
            <a href="mailto:support@ultimatefootballraffles.com" className="text-[color:var(--ufr-green)] font-bold">support@ultimatefootballraffles.com</a>{" "}
            and we will assist you.
          </p>
          <button
            onClick={resend}
            disabled={resending}
            className="btn-ufr-ghost px-5 py-2.5 text-xs mt-6"
            data-testid="resend-verification-btn"
          >
            {resending ? "Resending…" : "Resend verification email"}
          </button>
          <div className="text-center text-sm text-white/60 mt-6">
            Already verified? <Link to="/login" className="text-[color:var(--ufr-green)] font-bold">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="register-page">
      <div className="w-full max-w-md ufr-card rounded-md p-8">
        <div className="flex justify-center mb-6"><Logo size={56} /></div>
        <h1 className="font-display text-3xl text-white text-center">Join UFR</h1>
        <p className="text-white/60 text-sm text-center mt-1">Skill-based competitions — real card prizes.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold block mb-1.5">Full name</label>
            <input required minLength={2} className="ufr-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold block mb-1.5">Email</label>
            <input type="email" required className="ufr-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold block mb-1.5">Password</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} required minLength={6} className="ufr-input pr-11" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input" />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-[color:var(--ufr-green)] transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="register-password-toggle"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="text-[10px] text-white/40 mt-1">Min 6 characters.</div>
          </div>
          <button type="submit" disabled={loading} className="btn-ufr-primary w-full py-3 text-sm" data-testid="register-submit-btn">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="text-center text-sm text-white/60 mt-6">
          Already have an account? <Link to="/login" className="text-[color:var(--ufr-green)] font-bold" data-testid="register-login-link">Login</Link>
        </div>
      </div>
    </div>
  );
}
